import styled from '@emotion/styled';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TimeUtils from '@business/utils/time';
import DatePicker from '@data/models/DatePicker';
import { BEPopover, PopoverProps } from '@molecules/BEOffScreen/Popover';
import { useTranslation } from 'react-i18next';
import BEText from '@molecules/Text';

import Calendar from '@view/components/Calendar';
import { Box } from '@chakra-ui/react';
import Button from '@molecules/Button';
import { useLocation } from 'react-router';

interface CalendarProps {
  tag: 'checkin' | 'checkout';
  checkin?: Date;
  checkout?: Date;
  onSelectDate?: (value: DatePicker, tag: 'checkin' | 'checkout') => void;
}

function getDateString(date?: Date, locale = 'en-US') {
  if (!date) return '';

  if (locale === 'pt-BR') {
    const day: string = new Intl.DateTimeFormat(locale, {
      day: 'numeric',
    }).format(date);
    const month: string = new Intl.DateTimeFormat(locale, {
      month: 'short',
    })
      .format(date)
      .replace('.', '');
    const year: string = new Intl.DateTimeFormat(locale, {
      year: 'numeric',
    }).format(date);
    return `${day} ${month} ${year}`;
  }

  const formatOptions: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  };

  const res = Intl.DateTimeFormat(locale, formatOptions).format(date);
  return res;
}

const CalendarPopover = ({
  tag,
  checkin,
  checkout,
  onSelectDate,
  ...popoverProps
}: CalendarProps & PopoverProps) => {
  const { t } = useTranslation();

  const [checkinText, setCheckinText] = useState<string>(
    getDateString(checkin)
  );
  const [checkoutText, setCheckoutText] = useState<string>(
    getDateString(checkout)
  );

  const isMobile = popoverProps.renderMethod === 'fullscreen';

  const { pathname } = useLocation();
  const [path] = useState<string>(pathname);

  const onPickDate = useCallback(
    (value: DatePicker) => {
      setCheckinText(getDateString(value.checkin));
      setCheckoutText(getDateString(value.checkout));
      onSelectDate?.(value, tag);
    },
    [onSelectDate, tag]
  );

  useEffect(() => {
    if (pathname !== path) {
      popoverProps.onCancel?.();
    }
  }, [pathname, path, popoverProps]);

  return (
    <BEPopover {...popoverProps}>
      <StyledCalendarPopover isMobile={isMobile}>
        {isMobile && (
          <StyledCalendarHeader>
            <Box display={'flex'} gap={2} alignItems="center">
              <BEText type="h1" weight="600">
                {t('select-stay-dates')}
              </BEText>
            </Box>
            <BEText type="body" color={'neutral'}>
              {checkinText} - {checkoutText}
            </BEText>
          </StyledCalendarHeader>
        )}
        <StyledCalendarContainer isMobile={isMobile}>
          <Calendar
            onSelectDate={onPickDate}
            step={tag}
            isMobile={isMobile}
            checkin={checkin}
            checkout={checkout}
          />
        </StyledCalendarContainer>
        {isMobile && (
          <StyledCalendarFooter>
            <div className="description">
              <div className="dot"></div>
              <p>Restrictions may apply</p>
            </div>
            <Button color="primary" onClick={() => popoverProps.onOk?.()}>
              Apply
            </Button>
          </StyledCalendarFooter>
        )}
      </StyledCalendarPopover>
    </BEPopover>
  );
};

const StyledCalendarFooter = styled.div`
    width: 100%;
    height: 80px;
    min-height: 80px;
    padding: 0 24px;
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top: 1px solid #DDE0E4;
`;

const StyledCalendarHeader = styled.div`
    width: 100%;
    height: 80px;
    min-height: 80px;
    padding: 0 24px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    border-bottom: 1px solid #DDE0E4;
`;

const StyledCalendarContainer = styled.div<{ isMobile: boolean }>`
${({ isMobile }) => {
  if (isMobile) {
    return `
        overflow-y: scroll;
            flex-grow: 1;
            width: 100%;
        `;
  }
}}
`;

const StyledCalendarPopover = styled.div<{ isMobile: boolean }>`
    width: 754px;
    height: 450px;
    background: #FFFFFF;
    box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.2);
    border-radius: 12px;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;

    ${({ isMobile }) => {
      if (isMobile) {
        return `
                width: 100%;
                height: 100vh;
            `;
      }
    }}
`;

export default CalendarPopover;
